import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Package, ArrowRight, SealCheck, Crown, ChalkboardTeacher, Star,
    Check, Lightning, Sparkle, Confetti,
} from "@phosphor-icons/react";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); }
    catch { return "—"; }
};

const BENEFITS = [
    { icon: SealCheck, label: "Verified Badge", retail: 299,
      body: "Skip the KYC review queue — bundle buyers are auto-verified. 3× recruiter response rate.",
      tone: "text-emerald-600 bg-emerald-50 border-emerald-200" },
    { icon: Crown, label: "Pro Pass · 30 days", retail: 499,
      body: "Unlimited applications, AI CV rewrites, and priority recruiter matching.",
      tone: "text-cobalt bg-blue-50 border-blue-200" },
    { icon: ChalkboardTeacher, label: "Interview Prep · 5 credits", retail: 799,
      body: "Five AI mock-interview sessions tailored to your resume + target JD.",
      tone: "text-splash bg-splash/10 border-splash/30" },
    { icon: Star, label: "Featured · 30 days", retail: 99,
      body: "Gold star at the top of every recruiter search across all agencies.",
      tone: "text-amber-600 bg-amber-50 border-amber-200" },
];

export default function CandidateBundle() {
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(() => api.get("/me/bundle/all-four/status")
        .then((r) => setState(r.data))
        .catch(() => setState(null)), []);
    useEffect(() => { load(); }, [load]);

    const buy = async () => {
        setBusy(true);
        try {
            const rzOk = await new Promise((resolve) => {
                if (window.Razorpay) return resolve(true);
                const s = document.createElement("script");
                s.src = "https://checkout.razorpay.com/v1/checkout.js";
                s.onload = () => resolve(true);
                s.onerror = () => resolve(false);
                document.body.appendChild(s);
            });
            if (!rzOk) throw new Error("Couldn't load payment gateway");
            const { data: order } = await api.post("/me/bundle/all-four/checkout");
            const rz = new window.Razorpay({
                key: order.key_id,
                amount: order.amount_paise,
                currency: order.currency,
                order_id: order.order_id,
                name: "NaukriDaddy",
                description: "All-in-One Bundle · Verified + Pro + Prep + Featured",
                theme: { color: "#00D4E4" },
                handler: async (rp) => {
                    try {
                        await api.post("/me/bundle/all-four/verify", {
                            razorpay_order_id: rp.razorpay_order_id,
                            razorpay_payment_id: rp.razorpay_payment_id,
                            razorpay_signature: rp.razorpay_signature,
                        });
                        toast.success("Bundle unlocked! All 4 upgrades applied.");
                        load();
                    } catch { toast.error("Payment verification failed"); }
                },
                modal: { ondismiss: () => setBusy(false) },
            });
            rz.open();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't start checkout");
        } finally { setBusy(false); }
    };

    if (!state) return <div className="p-10 text-[#525252]">Loading…</div>;

    const already = state.latest_order?.status === "paid";
    const cs = state.current_state || {};

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="bundle-page">
            {/* Hero */}
            <div className="mb-8">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-splash mb-2 inline-flex items-center gap-1.5">
                    <Package size={11} weight="fill" /> Everything · one price · save ₹{state.savings_inr}
                </p>
                <h1 className="font-heading text-4xl sm:text-6xl font-black tracking-tighter leading-[0.95]">
                    Get all 4. <span className="text-cobalt">Save {state.savings_pct}%.</span>
                </h1>
                <p className="text-sm sm:text-base text-[#525252] mt-4 max-w-2xl">
                    The complete NaukriDaddy candidate stack for one clean price. Verified Badge,
                    Pro Pass, Interview Prep credits, and Featured placement — bundled for candidates
                    serious about their next role.
                </p>
            </div>

            {/* Just-purchased state */}
            {already && (
                <div data-testid="bundle-active-banner"
                     className="mb-8 bg-splash/10 border-2 border-splash p-6">
                    <div className="flex items-center gap-3">
                        <Confetti size={36} weight="fill" className="text-splash" />
                        <div className="flex-1">
                            <h2 className="font-heading text-2xl font-black text-ink">Bundle unlocked.</h2>
                            <p className="text-xs text-[#525252] mt-1">
                                Verified · Pro until {fmtDate(cs.pro_until)} · Featured until {fmtDate(cs.featured_until)} · {cs.prep_credits} prep credits
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Price card */}
            <div className="bg-ink text-white p-6 sm:p-8 mb-6" data-testid="bundle-price-card">
                <div className="flex items-end justify-between flex-wrap gap-4 mb-4">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-splash mb-1">One-time</p>
                        <div className="flex items-baseline gap-3">
                            <p className="font-heading text-6xl font-black tracking-tighter text-splash">₹{state.price_inr}</p>
                            <p className="text-lg text-white/50 line-through">₹{state.retail_inr}</p>
                        </div>
                        <p className="text-xs text-white/70 mt-1">
                            You save ₹{state.savings_inr} vs buying separately
                        </p>
                    </div>
                    <button onClick={buy} disabled={busy || already}
                            data-testid="bundle-buy-btn"
                            className="btn-gradient px-6 py-3 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
                        {busy ? "Loading…" : already ? "Already unlocked" : (<>Unlock the bundle <ArrowRight size={14} weight="bold" /></>)}
                    </button>
                </div>
                <div className="pt-4 border-t border-white/10 flex flex-wrap gap-4 text-[11px] text-white/70">
                    <span className="inline-flex items-center gap-1.5"><Lightning size={12} weight="fill" className="text-splash" /> Instant unlock</span>
                    <span className="inline-flex items-center gap-1.5"><Check size={12} weight="bold" className="text-splash" /> Skips KYC review</span>
                    <span className="inline-flex items-center gap-1.5"><Sparkle size={12} weight="fill" className="text-splash" /> Extends existing expiries</span>
                </div>
            </div>

            {/* What's inside */}
            <div className="grid gap-3 sm:grid-cols-2 mb-8">
                {BENEFITS.map((b, i) => (
                    <div key={i} data-testid={`bundle-benefit-${i}`}
                         className={`border ${b.tone.split(" ").slice(-1)} bg-white p-5`}>
                        <div className={`w-10 h-10 flex items-center justify-center mb-3 ${b.tone.split(" ").slice(0, 2).join(" ")}`}>
                            <b.icon size={18} weight="fill" />
                        </div>
                        <div className="flex items-baseline justify-between mb-1">
                            <p className="font-heading text-lg font-black text-ink">{b.label}</p>
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] line-through">₹{b.retail}</p>
                        </div>
                        <p className="text-sm text-[#404040] leading-relaxed">{b.body}</p>
                    </div>
                ))}
            </div>

            {/* Fine print */}
            <p className="text-[11px] text-[#A3A3A3] leading-relaxed">
                Bundle is a one-time charge — no auto-renewal. Pro Pass and Featured durations extend
                from your current expiry (early renewals never lose days). Interview Prep credits are
                credited instantly and never expire. Verified Badge skips manual KYC review because
                bundle purchase itself is a strong intent signal.
                See <a href="/policies/refund" className="text-cobalt underline">refund policy</a>.
            </p>
        </div>
    );
}
